/**
 * Source-agnostic SEO contract consumed by {@link SeoService}. It deliberately mirrors neither the
 * CMS shape ({@link LandingSeo}) nor the DOM meta tags directly: it is the *normalized* middle layer
 * so the service stays oblivious to where the data came from. CMS payloads are mapped into it by
 * `cmsSeoToPageSeo` (see `cms-seo.adapter.ts`); hand-authored pages can build it literally.
 */

/** Social networks with dedicated share tags (`og:*` for Facebook, `twitter:*` for Twitter). */
export enum SocialNetwork {
    Facebook = 'Facebook',
    Twitter = 'Twitter',
}

/** Per-network social override (Open Graph / Twitter card). */
export interface PageSeoSocial {
    readonly network: SocialNetwork;
    readonly title?: string | null;
    readonly description?: string | null;
    /** Absolute image URL (already resolved against any CMS base URL). */
    readonly imageUrl?: string | null;
}

/** Everything {@link SeoService} needs to describe a page, regardless of origin. */
export interface PageSeo {
    readonly title?: string | null;
    readonly description?: string | null;
    readonly keywords?: string | null;
    /** `robots` meta directive (e.g. `index,follow`). */
    readonly robots?: string | null;
    /** `viewport` meta override; rarely set per-page. */
    readonly viewport?: string | null;
    /** Absolute canonical URL; emitted as `<link rel="canonical">`. */
    readonly canonicalUrl?: string | null;
    /** Default share image (absolute URL), used for `og:image` / `twitter:image`. */
    readonly imageUrl?: string | null;
    /** JSON-LD structured data; serialized into a `<script type="application/ld+json">`. */
    readonly structuredData?: unknown;
    /** Per-network overrides; entries win over the page-level defaults above. */
    readonly social?: ReadonlyArray<PageSeoSocial>;
}

/**
 * Page-level title/description/image/canonical with all defaults already applied. Built by
 * `SeoService.setSeo` and used as the base for the Open Graph / Twitter tags (per-network `social`
 * overrides win over it). `canonicalUrl` is always resolved (CMS value or the current URL).
 */
export interface ResolvedPageSeo {
    readonly title: string | null;
    readonly description: string | null;
    readonly imageUrl: string | null;
    readonly canonicalUrl: string;
}
